import { queryClient } from '@/lib/query-client';
import { createQueryKeys } from '@/lib/query-factory';
import { CreateFeedbackPayload, Feedback } from '@certhon/models';
import {
  MutationOptions,
  queryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { listQueries } from '@/features/lists/lists.queries';
import * as api from './feedback.api';

const keys = createQueryKeys('feedback');

// ---------------------------------------------------------------------------
// Query options
// ---------------------------------------------------------------------------

export const feedbackQueries = {
  keys,

  forList: (protoListId: string) =>
    queryOptions({
      queryKey: keys.list({ protoListId }),
      queryFn: () => api.fetchFeedbackForList(protoListId),
      enabled: !!protoListId,
    }),

  forUser: (userId: number) =>
    queryOptions({
      queryKey: keys.list({ userId }),
      queryFn: () => api.fetchFeedbackForUser(userId),
      enabled: !!userId,
    }),
};

// ---------------------------------------------------------------------------
// Mutation options
// ---------------------------------------------------------------------------

export const feedbackMutations = {
  create: (): MutationOptions<Feedback, Error, CreateFeedbackPayload> => ({
    mutationFn: api.createFeedback,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: keys.list({ protoListId: variables.proto_list_id }),
      });
      queryClient.invalidateQueries({
        queryKey: keys.list({ userId: variables.user_id }),
      });
      // Templates table embeds feedback aggregate counts via PostgREST,
      // so refetch checklist/template lists to reflect new totals.
      queryClient.invalidateQueries({
        queryKey: listQueries.keys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: listQueries.keys.detail(variables.proto_list_id),
      });
      toast.success('Feedback submitted');
    },
    onError: (error: Error) => {
      toast.error('Failed to submit feedback', {
        description: error.message,
      });
    },
  }),

  markHandled: (): MutationOptions<
    Feedback,
    Error,
    { id: string; handledUserId: number }
  > => ({
    mutationFn: ({ id, handledUserId }) =>
      api.markFeedbackHandled(id, handledUserId),
    onSuccess: () => {
      // Invalidate all feedback lists since the handled item could appear in multiple views
      queryClient.invalidateQueries({ queryKey: keys.lists() });
      // Templates/checklists tables embed feedback_unresolved_count via PostgREST,
      // so refetch list queries to reflect the resolved item.
      queryClient.invalidateQueries({
        queryKey: listQueries.keys.lists(),
      });
      toast.success('Feedback marked as handled');
    },
    onError: (error: Error) => {
      toast.error('Failed to update feedback', {
        description: error.message,
      });
    },
  }),
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useFeedbackForList(protoListId: string) {
  return useQuery(feedbackQueries.forList(protoListId));
}

export function useFeedbackForUser(userId: number) {
  return useQuery(feedbackQueries.forUser(userId));
}

export function useCreateFeedbackMutation() {
  return useMutation(feedbackMutations.create());
}

export function useMarkFeedbackHandledMutation() {
  return useMutation(feedbackMutations.markHandled());
}
